import {
    OrderStepFields,
} from 'app/Apps/OrderManagement/Orders/orders.types'
import {
    LocationType,
    TransportModeSea,
    SeaFreightCarrier,
    TransportModeRoad,
    DefaultTruckingComment,
    DeliveryOptionRoad,
    SeaFreightContainerSize,
} from 'app/enums/label.enums'
import {
    FieldInCard,
    NestedField,
} from 'app/types/form.types'

type Args = {
    seaFreightContainerSize: SeaFreightContainerSize,
    forwarderId: number,
    transportModeRoad: TransportModeRoad,
    pickupLocationId: number,
    deliveryLocationId: number,
    filterLocationType: LocationType,
    transportModeSea: TransportModeSea,
    exists: boolean,
    isFirstStep: boolean,
    deliveryOptionRoad: DeliveryOptionRoad,
    getAllowedValues: (enumType: Record<string, string>) => Record<string, string>
}

const getInternalSeaFormFields = ({
    seaFreightContainerSize,
    forwarderId,
    transportModeRoad,
    pickupLocationId,
    deliveryLocationId,
    filterLocationType,
    transportModeSea,
    getAllowedValues,
    exists,
    isFirstStep,
    deliveryOptionRoad,
}: Args): FieldInCard<OrderStepFields>[] => {
    const trucksAndVans: FieldInCard<OrderStepFields>[] = [
        {
            name: 'deliveryOptionRoad',
            componentName: 'EnumSelector',
            allowedValues: getAllowedValues(DeliveryOptionRoad),
            required: true,
            className: 'deliveryOptionRoad',
            labelKey: 'OrderStep.deliveryOptionRoad',
        },
        ...deliveryOptionRoad === DeliveryOptionRoad.TRUCKS ? [{
            name: 'numberOfTrucks',
            componentName: 'IntegerInput',
            className: 'numberOfVehicles',
            required: true,
            labelKey: 'OrderStep.numberOfTrucks',
        }] as FieldInCard<OrderStepFields>[] : [],
        ...deliveryOptionRoad === DeliveryOptionRoad.VANS ? [{
            name: 'numberOfVans',
            componentName: 'IntegerInput',
            className: 'numberOfVehicles',
            required: true,
            labelKey: 'OrderStep.numberOfVans',
        }] as FieldInCard<OrderStepFields>[] : [],
    ]

    const pickupLocationContactsByLocationField: NestedField<OrderStepFields>[] = [{
        name: 'pickupLocationContacts',
        componentName: 'MultipleContactByLocationObjectSelector',
        existedForm: exists,
        selectedLocation: pickupLocationId,
        labelKey: 'OrderStep.pickupLocationContacts',
    }]

    const pickupLocationContactsField: NestedField<OrderStepFields>[] = [{
        name: 'pickupLocationContacts',
        componentName: 'MultipleContactObjectSelector',
        labelKey: 'OrderStep.pickupLocationContacts',
    }]

    const seaFreightContainerSizeFields: NestedField<OrderStepFields>[] = [
        {
            name: 'seaFreightContainerSize',
            componentName: 'EnumSelector',
            allowedValues: getAllowedValues(SeaFreightContainerSize),
            noSort: true,
            required: true,
            labelKey: 'OrderStep.seaFreightContainerSize',
        },
        ...(seaFreightContainerSize === SeaFreightContainerSize.TWENTY_FEET)
        || (seaFreightContainerSize === SeaFreightContainerSize.TWENTY_AND_FORTY_FEET) ? [{
            name: 'numberOf20FeetContainers',
            componentName: 'IntegerInput',
            required: true,
            className: seaFreightContainerSize === SeaFreightContainerSize.TWENTY_AND_FORTY_FEET ? 'deliveryOptionRoad' : null,
            labelKey: 'OrderStep.numberOf20FeetContainers',
        }] as NestedField<OrderStepFields>[] : [],
        ...(seaFreightContainerSize === SeaFreightContainerSize.FORTY_FEET)
        || (seaFreightContainerSize === SeaFreightContainerSize.TWENTY_AND_FORTY_FEET) ? [{
            name: 'numberOf40FeetContainers',
            componentName: 'IntegerInput',
            className: seaFreightContainerSize === SeaFreightContainerSize.TWENTY_AND_FORTY_FEET ? 'numberOfVehicles' : null,
            required: true,
            labelKey: 'OrderStep.numberOf40FeetContainers',
        }] as NestedField<OrderStepFields>[] : [],
    ]

    return [
        {
            id: 'column1',
            fields: [
                {
                    name: 'forwarder',
                    componentName: 'FreightForwarderSelector',
                    labelKey: 'OrderStep.forwarder',
                },
                {
                    name: 'forwarderContacts',
                    componentName: 'MultipleContactByLocationObjectSelector',
                    existedForm: exists,
                    selectedLocation: forwarderId,
                    labelKey: 'OrderStep.forwarderContacts',
                },
                {
                    name: 'forwarderAdHoc',
                    componentName: 'InputSingleline',
                    labelKey: 'OrderStep.forwarderAdHoc',
                },
                {
                    name: 'transportModeSea',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(TransportModeSea),
                    labelKey: 'OrderStep.transportModeSea',
                },
                ...transportModeSea === TransportModeSea.FCL ? seaFreightContainerSizeFields : [],
                {
                    name: 'mbolReceived',
                    componentName: 'Checkbox',
                    labelKey: 'OrderStep.mbolReceived',
                },
                {
                    name: 'mbolNumber',
                    componentName: 'InputSingleline',
                    labelKey: 'OrderStep.mbolNumber',
                },
                {
                    name: 'mbolDocumentCopy',
                    componentName: 'AttachmentsObjectSelector',
                    labelKey: 'OrderStep.mbolDocumentCopy',
                },
                {
                    name: 'transportModeRoad',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(TransportModeRoad),
                    labelKey: 'OrderStep.transportModeRoad',
                },
                ...transportModeRoad === TransportModeRoad.FTL ? trucksAndVans as [] : [],
                {
                    name: 'isTemperatureControlled',
                    componentName: 'Checkbox',
                    labelKey: 'OrderStep.isTemperatureControlled',
                },
                {
                    name: 'defaultTruckingComment',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(DefaultTruckingComment),
                    useDropdown: true,
                    labelKey: 'OrderStep.defaultTruckingComment',
                },
                {
                    name: 'truckingComment',
                    componentName: 'LongText',
                    className: 'truckingComment',
                    labelKey: 'OrderStep.truckingComment',
                },
            ],
        },
        {
            id: 'column2',
            fields: [
                {
                    name: 'pickupLocation',
                    componentName: 'LocationObjectSelector',
                    isShippingLocation: true,
                    labelKey: 'OrderStep.pickupLocation',
                },
                ...isFirstStep
                    ? pickupLocationContactsByLocationField
                    : pickupLocationContactsField,
                {
                    name: 'requestedPickup',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: pickupLocationId,
                    labelKey: 'OrderStep.requestedPickup',
                },
                {
                    name: 'estimatedPickup',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: pickupLocationId,
                    labelKey: 'OrderStep.estimatedPickup',
                },
                {
                    name: 'actualPickup',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: pickupLocationId,
                    labelKey: 'OrderStep.actualPickup',
                },
                {
                    name: 'pickupComment',
                    componentName: 'LongText',
                    labelKey: 'OrderStep.pickupComment',
                },
                {
                    name: 'hbolReceived',
                    componentName: 'Checkbox',
                    labelKey: 'OrderStep.hbolReceived',
                },
                {
                    name: 'hbolNumber',
                    componentName: 'InputSingleline',
                    labelKey: 'OrderStep.hbolNumber',
                },
                {
                    name: 'hbolDocumentCopy',
                    componentName: 'AttachmentsObjectSelector',
                    labelKey: 'OrderStep.hbolDocumentCopy',
                },
            ],
        },
        {
            id: 'column3',
            fields: [
                {
                    name: 'deliveryLocation',
                    componentName: 'LocationObjectSelector',
                    isShippingLocation: true,
                    labelKey: 'OrderStep.deliveryLocation',
                },
                {
                    name: 'deliveryLocationContacts',
                    componentName: 'MultipleContactByLocationObjectSelector',
                    existedForm: exists,
                    selectedLocation: deliveryLocationId,
                    labelKey: 'OrderStep.deliveryLocationContacts',
                },
                {
                    name: 'requestedDelivery',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: deliveryLocationId,
                    labelKey: 'OrderStep.requestedDelivery',
                },
                {
                    name: 'estimatedDelivery',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: deliveryLocationId,
                    labelKey: 'OrderStep.estimatedDelivery',
                },
                {
                    name: 'actualDelivery',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: deliveryLocationId,
                    labelKey: 'OrderStep.actualDelivery',
                },
                {
                    name: 'deliveryComment',
                    componentName: 'LongText',
                    labelKey: 'OrderStep.deliveryComment',
                },
            ],
        },
        {
            id: 'column4',
            fields: [
                {
                    name: 'seaFreightCarrier',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(SeaFreightCarrier),
                    labelKey: 'OrderStep.seaFreightCarrier',
                },
                {
                    name: 'originSeaport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType,
                    labelKey: 'OrderStep.originSeaport',
                },
                {
                    name: 'destinationSeaport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType,
                    labelKey: 'OrderStep.destinationSeaport',
                },
                {
                    name: 'actualShipDeparture',
                    componentName: 'DateTimeZonedSelect',
                    labelKey: 'OrderStep.actualShipDeparture',
                },
                {
                    name: 'actualShipArrival',
                    componentName: 'DateTimeZonedSelect',
                    labelKey: 'OrderStep.actualShipArrival',
                },
                {
                    name: 'seaFreightComment',
                    componentName: 'LongText',
                    labelKey: 'OrderStep.seaFreightComment',
                },
            ],
        },
    ]
}

export default getInternalSeaFormFields
